import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, TrendingDown, Package, DollarSign } from 'lucide-react';
import { userAPI } from '../../services/apiService';

const PerformanceComparison = () => {
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadComparison();
  }, []);

  const loadComparison = async () => {
    try {
      const data = await userAPI.getPerformanceComparison();
      setComparison(data);
    } catch (error) {
      console.error('Error loading performance comparison:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !comparison) {
    return (
      <Card className="border-3 border-indigo-300 shadow-lg bg-white/90 backdrop-blur-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { currentMonth, lastMonth, changes } = comparison;

  return (
    <Card className="border-3 border-indigo-300 shadow-lg bg-white/90 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg">Performance Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Orders Comparison */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-sm">Orders</span>
              </div>
              <div className="flex items-center gap-1">
                {changes.orders >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm font-bold ${
                  changes.orders >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.orders >= 0 ? '+' : ''}{changes.orders}%
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-xs text-gray-600">{currentMonth.month}</p>
                <p className="text-2xl font-bold text-blue-600">{currentMonth.orders}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">{lastMonth.month}</p>
                <p className="text-2xl font-bold text-gray-400">{lastMonth.orders}</p>
              </div>
            </div>
          </div>

          {/* Earnings Comparison */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-sm">Earnings</span>
              </div>
              <div className="flex items-center gap-1">
                {changes.earnings >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm font-bold ${
                  changes.earnings >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.earnings >= 0 ? '+' : ''}{changes.earnings}%
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-xs text-gray-600">{currentMonth.month}</p>
                <p className="text-2xl font-bold text-green-600">₹{currentMonth.earnings.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">{lastMonth.month}</p>
                <p className="text-2xl font-bold text-gray-400">₹{lastMonth.earnings.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceComparison;